
import React, { useState, useEffect } from 'react';
import { Loader2, Send, CheckCircle2, AlertCircle, MapPin, Phone, User as UserIcon, Calendar, Mail, Briefcase, Camera, ArrowRight, ArrowLeft, Globe, X, FileWarning } from 'lucide-react';
import { supabase } from '../supabase';
import { CONFIG } from '../config';
import { Attachment, AttendanceStatus } from '../types';

interface PublicAttendanceFormProps {
  containerSlug: string;
}

type Step = 'loading' | 'not-found' | 'identification' | 'request' | 'success' | 'fatal-error';

const inputClass = "w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-800 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-emerald-500";
const labelClass = "text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1";

const PublicAttendanceForm: React.FC<PublicAttendanceFormProps> = ({ containerSlug }) => {
  const [step, setStep] = useState<Step>('loading');
  const [container, setContainer] = useState<{ id: string; name: string } | null>(null);
  const [loadingCep, setLoadingCep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [identForm, setIdentForm] = useState({
    name: '', phone: '', birthdate: '', email: '', profession: '',
    zipcode: '', street: '', number: '', complement: '', neighborhood: '', city: ''
  });

  const [requestForm, setRequestForm] = useState({ title: '', description: '' });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc('get_public_container', { p_slug: containerSlug });
        if (rpcError || !data || data.length === 0) { setStep('not-found'); return; }
        setContainer(data[0]);
        setStep('identification');
      } catch (e) {
        setStep('not-found');
      }
    };
    load();
  }, [containerSlug]);

  const handleCepBlur = async () => {
    const cep = identForm.zipcode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setIdentForm(prev => ({ ...prev, street: data.logradouro, neighborhood: data.bairro, city: data.localidade }));
      }
    } catch (err) { console.error(err); } finally { setLoadingCep(false); }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
  };

  const canContinue = identForm.name.trim() && identForm.phone.trim() && identForm.birthdate.trim();
  const canSubmit = requestForm.title.trim() && requestForm.description.trim();

  const handleSubmit = async () => {
    if (!container || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const phone = identForm.phone.trim();
      let contactId: string | null = null;

      const { data: foundId } = await supabase.rpc('find_contact_id_by_phone', { p_container_id: container.id, p_phone: phone });
      contactId = foundId || null;

      if (!contactId) {
        contactId = crypto.randomUUID();
        let lat: number | null = null, lng: number | null = null;
        if (identForm.street && identForm.city) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3500);
          try {
            const response = await fetch(`${CONFIG.supabaseUrl}/functions/v1/geocode-address`, {
              method: 'POST', signal: controller.signal,
              headers: { 'Content-Type': 'application/json', apikey: CONFIG.supabaseAnonKey, Authorization: `Bearer ${CONFIG.supabaseAnonKey}` },
              body: JSON.stringify({ street: identForm.street, number: identForm.number, neighborhood: identForm.neighborhood, city: identForm.city, zipcode: identForm.zipcode })
            });
            if (response.ok) { const geoData = await response.json(); if (geoData?.ok) { lat = geoData.latitude; lng = geoData.longitude; } }
          } catch (err) { console.warn(err); } finally { clearTimeout(timeoutId); }
        }

        const { error: contactError } = await supabase.from('contacts').insert([{
          id: contactId,
          container_id: container.id,
          name: identForm.name,
          type: 'Cidadão',
          support_level: 'Neutro',
          profession: identForm.profession || null,
          birthdate: identForm.birthdate || null,
          phones: phone ? [phone] : [],
          emails: identForm.email ? [identForm.email] : [],
          city: identForm.city,
          neighborhood: identForm.neighborhood,
          street: identForm.street,
          number: identForm.number,
          complement: identForm.complement,
          zipcode: identForm.zipcode,
          contact_origin: 'Formulário Público',
          tags: [],
          observations: '',
          latitude: lat,
          longitude: lng
        }]);
        if (contactError) throw contactError;
      }

      const attachments: Attachment[] = [];
      if (photo) {
        const fileExt = photo.name.split('.').pop();
        const filePath = `${container.id}/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage.from('public-uploads').upload(filePath, photo);
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('public-uploads').getPublicUrl(filePath);
        attachments.push({
          id: crypto.randomUUID(),
          name: photo.name,
          url: urlData.publicUrl,
          type: 'image',
          size: photo.size,
          timestamp: new Date().toISOString()
        });
      }

      const { error: attError } = await supabase.from('attendances').insert([{
        id: crypto.randomUUID(),
        container_id: container.id,
        contactid: contactId,
        demand: requestForm.title,
        summary: requestForm.description,
        status: AttendanceStatus.PENDING,
        urgency: 'Média',
        channel: 'Formulário Externo',
        source: 'public_form',
        viewed_at: null,
        attachments
      }]);
      if (attError) throw attError;

      setStep('success');
    } catch (err: any) {
      console.error('[PublicAttendanceForm] erro ao enviar:', err);
      setError('Não foi possível enviar seu pedido. Verifique os dados e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'loading') {
    return <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-navy-950"><Loader2 className="animate-spin text-emerald-600" size={40} /></div>;
  }

  if (step === 'not-found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-navy-950 px-4">
        <div className="text-center space-y-4">
          <FileWarning className="mx-auto text-slate-300" size={64} />
          <h1 className="text-xl font-black text-navy-950 dark:text-white uppercase tracking-widest">Página não encontrada</h1>
          <p className="text-sm text-slate-500 font-medium">O link acessado não corresponde a nenhum gabinete ativo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-navy-950 flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="bg-navy-900 dark:bg-emerald-600 p-2 rounded-xl text-white shadow-lg"><Globe size={22} /></div>
          <span className="font-black text-lg tracking-tight uppercase dark:text-white text-center">{container?.name}</span>
        </div>

        <div className="bg-white dark:bg-navy-900 rounded-[32px] shadow-2xl border border-neutral-200 dark:border-navy-800 p-6 md:p-10">
          {step === 'success' ? (
            <div className="text-center space-y-4 py-8 animate-in fade-in">
              <CheckCircle2 className="mx-auto text-emerald-500" size={64} />
              <h2 className="text-xl font-black text-navy-950 dark:text-white uppercase tracking-widest">Pedido recebido!</h2>
              <p className="text-sm text-slate-500 font-medium max-w-md mx-auto">Recebemos sua solicitação. Nossa equipe irá analisá-la e entrar em contato em breve.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${step === 'identification' ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center ${step === 'identification' ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-navy-800 text-slate-400'}`}>1</span> Seus dados
                </div>
                <div className="w-10 h-px bg-slate-200 dark:bg-navy-800" />
                <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${step === 'request' ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center ${step === 'request' ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-navy-800 text-slate-400'}`}>2</span> Seu pedido
                </div>
              </div>

              {error && <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold mb-6 flex items-center gap-2"><AlertCircle size={16}/> {error}</div>}

              {step === 'identification' && (
                <div className="space-y-8 animate-in slide-in-from-right-4">
                  <div className="space-y-6">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-emerald-600 pl-3">Dados Pessoais</p>
                    <div className="space-y-2">
                      <label className={labelClass}><UserIcon size={12} className="inline mr-1"/> Nome Completo *</label>
                      <input required value={identForm.name} onChange={e => setIdentForm({...identForm, name: e.target.value})} className={inputClass} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={labelClass}><Phone size={12} className="inline mr-1"/> Telefone *</label>
                        <input required value={identForm.phone} onChange={e => setIdentForm({...identForm, phone: e.target.value})} placeholder="(00) 00000-0000" className={inputClass} />
                      </div>
                      <div className="space-y-2">
                        <label className={labelClass}><Calendar size={12} className="inline mr-1"/> Data de Nascimento *</label>
                        <input required type="date" value={identForm.birthdate} onChange={e => setIdentForm({...identForm, birthdate: e.target.value})} className={inputClass} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={labelClass}><Mail size={12} className="inline mr-1"/> E-mail</label>
                        <input type="email" value={identForm.email} onChange={e => setIdentForm({...identForm, email: e.target.value})} placeholder="exemplo@email.com" className={inputClass} />
                      </div>
                      <div className="space-y-2">
                        <label className={labelClass}><Briefcase size={12} className="inline mr-1"/> Profissão</label>
                        <input value={identForm.profession} onChange={e => setIdentForm({...identForm, profession: e.target.value})} className={inputClass} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-parliament-600 pl-3">Endereço (opcional)</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className={labelClass}><MapPin size={12} className="inline mr-1"/> CEP</label>
                        <div className="relative">
                          <input value={identForm.zipcode} onChange={e => setIdentForm({...identForm, zipcode: e.target.value})} onBlur={handleCepBlur} className={`${inputClass} pr-10`} />
                          {loadingCep && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-emerald-600"/>}
                        </div>
                      </div>
                      <div className="col-span-2 space-y-2">
                        <label className={labelClass}>Cidade</label>
                        <input value={identForm.city} onChange={e => setIdentForm({...identForm, city: e.target.value})} className={inputClass} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass}>Logradouro</label>
                      <div className="flex gap-2">
                        <input value={identForm.street} onChange={e => setIdentForm({...identForm, street: e.target.value})} placeholder="Rua..." className={`flex-[3] ${inputClass}`} />
                        <input value={identForm.number} onChange={e => setIdentForm({...identForm, number: e.target.value})} placeholder="Nº" className={`flex-1 ${inputClass}`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={labelClass}>Bairro</label>
                        <input value={identForm.neighborhood} onChange={e => setIdentForm({...identForm, neighborhood: e.target.value})} className={inputClass} />
                      </div>
                      <div className="space-y-2">
                        <label className={labelClass}>Complemento</label>
                        <input value={identForm.complement} onChange={e => setIdentForm({...identForm, complement: e.target.value})} className={inputClass} />
                      </div>
                    </div>
                  </div>

                  <button disabled={!canContinue} onClick={() => setStep('request')} className="w-full bg-navy-950 dark:bg-emerald-600 text-white py-5 rounded-2xl font-black text-xs uppercase shadow-xl flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                    Continuar <ArrowRight size={18}/>
                  </button>
                </div>
              )}

              {step === 'request' && (
                <div className="space-y-8 animate-in slide-in-from-right-4">
                  <div className="space-y-6">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-emerald-600 pl-3">Descreva sua solicitação</p>
                    <div className="space-y-2">
                      <label className={labelClass}>Título do Pedido *</label>
                      <input required value={requestForm.title} onChange={e => setRequestForm({...requestForm, title: e.target.value})} placeholder="Ex: Buraco na rua, falta de iluminação..." className={inputClass} />
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass}>Descrição *</label>
                      <textarea required value={requestForm.description} onChange={e => setRequestForm({...requestForm, description: e.target.value})} rows={5} className={`${inputClass} font-medium`} />
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass}><Camera size={12} className="inline mr-1"/> Foto (opcional)</label>
                      {photoPreview ? (
                        <div className="relative inline-block">
                          <img src={photoPreview} alt="Pré-visualização" className="h-32 rounded-xl border border-neutral-200 dark:border-navy-800 object-cover" />
                          <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg"><X size={14}/></button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-neutral-200 dark:border-navy-800 rounded-xl py-6 cursor-pointer hover:bg-slate-50 dark:hover:bg-navy-950 transition-colors text-slate-400 text-xs font-bold uppercase tracking-widest">
                          <Camera size={18}/> Anexar foto
                          <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setStep('identification')} disabled={submitting} className="px-6 py-5 rounded-2xl font-black text-xs uppercase bg-neutral-100 dark:bg-navy-800 text-slate-500 dark:text-slate-300 hover:bg-neutral-200 transition-all flex items-center gap-2">
                      <ArrowLeft size={18}/> Voltar
                    </button>
                    <button disabled={!canSubmit || submitting} onClick={handleSubmit} className="flex-1 bg-emerald-600 text-white py-5 rounded-2xl font-black text-xs uppercase shadow-xl flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                      {submitting ? <Loader2 className="animate-spin" size={18}/> : <Send size={18}/>} Enviar Pedido
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-center text-[10px] font-black text-slate-400 uppercase tracking-widest mt-6">Gabinete Digital</p>
      </div>
    </div>
  );
};

export default PublicAttendanceForm;
